import { useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { PieceRunSummary } from "@/api/types";

/**
 * ef-060-8 — the piece tickets behind ONE pay run (`GET /payroll/runs/{id}/piece`).
 *
 * Server state, fetched here and never copied into the pay run hook: `usePayRuns` owns the run, this
 * owns what the run's piece tickets say. `refreshKey` is how the screen re-reads after a write that
 * changes what the answer means (finalize flips draft "will be added" into "included"; void releases
 * the tickets) without this hook knowing about those states.
 *
 * `runId === null` (nothing applied yet) fetches nothing and reads as "no piece pay".
 */
export interface UsePayRunPiece {
  summary: PieceRunSummary | null;
  /** Set when the read failed; the section says so rather than quietly showing nothing. */
  error: string | null;
}

export function usePayRunPiece(runId: string | null, refreshKey: string): UsePayRunPiece {
  const [state, setState] = useState<{ runId: string; key: string; summary: PieceRunSummary | null; error: string | null } | null>(null);

  useEffect(() => {
    if (runId === null) return;
    const controller = new AbortController();
    void (async () => {
      try {
        const summary = await demoApi.getPayRunPiece(runId, controller.signal);
        if (controller.signal.aborted) return;
        setState({ runId, key: refreshKey, summary, error: null });
      } catch (err) {
        if (controller.signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setState({ runId, key: refreshKey, summary: null, error: problemMessage(err, "Could not load this run's piece pay.") });
      }
    })();
    return () => controller.abort();
  }, [runId, refreshKey]);

  // A result for a different run is stale the moment the screen moves on: derive, never carry it over.
  if (runId === null || state === null || state.runId !== runId) return { summary: null, error: null };
  return { summary: state.summary, error: state.error };
}
